<script lang="ts" setup>
import { ElMessage } from 'element-plus'
import useClipboard from 'vue-clipboard3/dist/esm/index'
import { ArrowDown } from '@element-plus/icons-vue'
import { DateTime } from 'luxon'
import { useCancelAppointments } from '~/compositions/cancelAppointments'
import {
  AppointmentStatus,
  AppointmentsApi,
  GETFileExportFileTypeEnum,
} from '~/services/apiClient'
import type { AppointmentWithContainerInfo } from '~/models/appointmentWithContainerInfo'

const props = defineProps<{
  selectedAppointments: AppointmentWithContainerInfo[]
}>()

// Computed properties
const selectedContainerNumbers = computed(() => {
  const containerNumbers = new Set<string>()
  for (const row of props.selectedAppointments) {
    if (row.container_number) {
      containerNumbers.add(row.container_number)
    }
  }
  return Array.from(containerNumbers)
})

const someRowsSelected = computed(() => props.selectedAppointments.length > 0)

const menuTooltip = computed(() => {
  if (props.selectedAppointments.length > 0) {
    return `Perform actions on ${props.selectedAppointments.length} selected appointments`
  } else {
    return 'Select some appointments to perform actions'
  }
})

// Functions
const { toClipboard } = useClipboard()

async function copyContainerNumbers() {
  // this is a safety check and pops a message to the user
  if (!ensureSelectedRows()) return
  const containers = selectedContainerNumbers.value.join('\n')

  if (containers.trim() === '') {
    ElMessage.warning('No container numbers to copy')
    return
  }

  try {
    await toClipboard(containers)
    ElMessage({
      message: `Copied ${selectedContainerNumbers.value.length} container #'s to clipboard`,
      type: 'success',
    })
  } catch (fallbackError) {
    ElMessage({
      message: `Failed to copy to clipboard - please copy manually`,
      type: 'error',
    })
  }
}

function ensureSelectedRows() {
  if (!someRowsSelected.value) {
    ElMessage.warning('You must select one or more appointments')
    return false
  } else {
    return true
  }
}

const { cancelAppointmentsScheduledView } = useCancelAppointments()
function triggerCancelAppointments() {
  const filteredAppointments = props.selectedAppointments.filter((row) => {
    return (
      row.appointment.status === AppointmentStatus.Scheduled ||
      row.appointment.status === AppointmentStatus.InProgress
    )
  })
  if (filteredAppointments.length === 0) {
    ElMessage.warning('You must select one or more appointments')
    return
  }
  cancelAppointmentsScheduledView(
    filteredAppointments.map((row) => row.appointment)
  )
}

const downloading = ref(false)
function download(fileType: GETFileExportFileTypeEnum, ids: number[]) {
  downloading.value = true
  const now = DateTime.now()
  const fileName = `appointments ${now.toFormat('yyyy-MM-dd HH_mm_ss')}.${fileType}`
  const api = new AppointmentsApi()
  api
    .fileExportAppointmentsFileExportPost(
      {
        file_type: fileType,
        to_trinium: false,
        appointment_ids: ids,
      },
      { responseType: 'blob' }
    )
    .then((resp) => {
      if (resp.status === 200) {
        const blob = new Blob([resp.data], {
          type: resp.headers['content-type'],
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    })
    .catch(() => {
      ElMessage.error('Failed to download appointments file.')
    })
    .finally(() => {
      downloading.value = false
    })
}
</script>

<template>
  <div>
    <div class="flex justify-between">
      <el-dropdown v-tooltip="menuTooltip" trigger="click">
        <el-button type="success" size="small" :disabled="!someRowsSelected">
          Actions
          <el-icon class="el-icon--right"><ArrowDown /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <!-- Copy Container Numbers -->
            <el-dropdown-item
              id="copy-selected-container-numbers-appts"
              @click="copyContainerNumbers"
            >
              <i-ic:round-content-copy class="mr-2" />
              Copy All Container #'s
            </el-dropdown-item>
            <!-- Cancel Appointments -->
            <el-dropdown-item @click="triggerCancelAppointments">
              <i-mdi:calendar-remove class="mr-2" />
              Cancel Appointments
            </el-dropdown-item>

            <!-- Download Appointments -->
            <el-dropdown-item
              @click="
                download(
                  GETFileExportFileTypeEnum.Csv,
                  selectedAppointments.map((row) => row.appointment.id)
                )
              "
            >
              <i-eva:cloud-download-outline class="mr-2" />
              Download Appointments (CSV)
            </el-dropdown-item>
            <el-dropdown-item
              @click="
                download(
                  GETFileExportFileTypeEnum.Xlsx,
                  selectedAppointments.map((row) => row.appointment.id)
                )
              "
            >
              <i-eva:cloud-download-outline class="mr-2" />
              Download Appointments (Excel)
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>
